import React, { useState } from 'react'
import { sheetAtom } from 'atoms/atoms'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  ActionListItem,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Box as Table,
  Box as TableBody,
} from '@node-space/ui-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactInstrument } from 'types/contacts'
import { SheetName, SheetState } from 'types/types'
import { contactInstrumentAtom } from './atoms/contactAtoms'
import { ContactInstrumentDeleteModal } from './components/ContactInstrumentDeleteModal'

interface ContactInstrumentsListProps {
  contactInstruments: ContactInstrument[]
}

export const ContactInstrumentsList = ({ contactInstruments }: ContactInstrumentsListProps) => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const [isDeleteInstrumentModalOpen, setIsDeleteInstrumentModalOpen] = useState(false)
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const setSheet = useSetAtom(sheetAtom)

  const onDetailsClick = (contactInstrument: ContactInstrument) => {
    // TODO: add details action
  }

  const onEditClick = (contactInstrument: ContactInstrument) => {
    setContactInstrument(contactInstrument)
    setSheet({
      [SheetName.INSTRUMENT]: SheetState.OPEN,
    })
  }

  const onDeleteClick = (contactInstrument: ContactInstrument) => {
    setContactInstrument(contactInstrument)
    setIsDeleteInstrumentModalOpen(true)
  }

  const onDeleteModalClose = () => {
    setIsDeleteInstrumentModalOpen(false)
    setContactInstrument(null)
  }

  return (
    <Box hasBorder divideChildren>
      <Table tag="table" width="full" data-testid="contact-instruments-list">
        <TableBody tag="tbody" width="full">
          {contactInstruments?.map(contactInstrument => {
            const { id, description, currency, address, network } = contactInstrument || {}

            return (
              <ActionListItem
                key={id}
                id={id}
                title={description}
                CustomAction={
                  <>
                    <DropdownMenu
                      DropdownMenuItems={[
                        ...(canEdit && !viewOnly
                          ? [
                              <DropdownMenuItem
                                onClick={() => onEditClick(contactInstrument)}
                                key={`action-edit-${id}`}
                                LeadingIcon={<Icon name="SimplePencilCustom" />}
                              >
                                {t('edit')}
                              </DropdownMenuItem>,
                            ]
                          : []),
                        <DropdownMenuItem
                          key={`action-info-${id}`}
                          LeadingIcon={<Icon name="Info" />}
                          onClick={() => onDetailsClick(contactInstrument)}
                        >
                          {t('details')}
                        </DropdownMenuItem>,
                        ...(canEdit && !viewOnly
                          ? [
                              <DropdownMenuItem
                                key={`action-delete-${id}`}
                                LeadingIcon={<Icon name="Trash" />}
                                onClick={() => onDeleteClick(contactInstrument)}
                              >
                                {t('delete')}
                              </DropdownMenuItem>,
                            ]
                          : []),
                      ]}
                    >
                      <Button
                        iconElement={<Icon name="DotsThreeVerticalCustom" />}
                        variant="ghost"
                      />
                    </DropdownMenu>
                  </>
                }
                border
                Avatar={<Avatar type="currency" name={currency?.toLowerCase()} />}
                BodyPre={
                  <p className="text-sm text-generic-foreground-subdued leading-tight font-normal">
                    {`${currency} (${network}) - ${address}`}
                  </p>
                }
              />
            )
          })}
        </TableBody>
      </Table>

      <ContactInstrumentDeleteModal
        isModalOpen={isDeleteInstrumentModalOpen}
        onModalClose={onDeleteModalClose}
      />
    </Box>
  )
}
